import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { UserRound, Home, TrendingUp, Receipt, Sparkles } from "lucide-react";
import {
  SUB_LABEL,
  RENTAL_INCOME_SUBS,
  RENTAL_EXPENSE_SUBS,
  INVESTMENT_TYPES,
  DEDUCTION_SUBS,
  WFH_RATE_PER_HOUR,
  WFH_RATE_LABEL,
} from "@/components/personal-tax/constants";
import BiSelect from "@/components/BiSelect";

/**
 * PersonalExpenseToggle — surfaces on the Review screen so a tradie who snapped
 * a rent statement / dividend notice / self-education receipt can classify it
 * as PERSONAL right from the capture flow. When on, Review's Approve button
 * routes to POST /api/personal-tax/entries instead of the business entries
 * pipeline — carrying the parsed supplier/total/gst/date/photo_path across.
 *
 * Emits an object via onChange(payload) whenever the user tweaks any field:
 *   { on, type, sub_category, property_id?, hours?, payer? }
 *
 * The parent decides how to submit. This component only owns the UI + local
 * state + property list fetch.
 */

const TOP_KINDS = [
  { key: "rental_income", label: "Rental income", icon: Home, color: "text-[#25D366]" },
  { key: "rental_expense", label: "Rental expense", icon: Home, color: "text-[#F97316]" },
  { key: "investment", label: "Investment income", icon: TrendingUp, color: "text-[#60A5FA]" },
  { key: "deduction", label: "Personal deduction", icon: Receipt, color: "text-[#FF9500]" },
];

const asOptions = (keys) => keys.map((v) => ({ v, l: SUB_LABEL[v] || v }));

export default function PersonalExpenseToggle({ suggestion, onChange, initialOn = false }) {
  const [on, setOn] = useState(Boolean(initialOn));
  const [topKind, setTopKind] = useState("deduction");
  const [subCategory, setSubCategory] = useState("other");
  const [investKind, setInvestKind] = useState("dividend|dividend"); // "type|sub"
  const [propertyId, setPropertyId] = useState("");
  const [hours, setHours] = useState("");
  const [properties, setProperties] = useState([]);
  const [suggestionUsed, setSuggestionUsed] = useState(false);

  // Fetch rental properties once when toggled on
  useEffect(() => {
    if (!on || properties.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get("/personal-tax/properties");
        setProperties(data || []);
        if (data && data.length > 0 && !propertyId) setPropertyId(data[0].id);
      } catch (err) {
        devLog.warn("properties load failed", err);
      }
    })();
  }, [on, properties.length, propertyId]);

  // Apply AI suggestion once when it arrives (e.g. from parsed docket hints)
  useEffect(() => {
    if (!suggestion || suggestionUsed) return;
    if (suggestion.top_kind) setTopKind(suggestion.top_kind);
    if (suggestion.sub_category) setSubCategory(suggestion.sub_category);
    setSuggestionUsed(true);
  }, [suggestion, suggestionUsed]);

  // Derived (type, sub) shipping to parent
  const payload = useMemo(() => {
    let type = topKind === "rental_income" || topKind === "rental_expense" ? topKind : null;
    let sub = subCategory;
    if (topKind === "investment") {
      const [t, s] = investKind.split("|");
      type = t;
      sub = s;
    } else if (topKind === "deduction") {
      type = "deduction";
    }
    return {
      on,
      type,
      sub_category: sub,
      property_id: type && type.startsWith("rental") ? propertyId || null : null,
      hours: sub === "wfh_hours" && hours ? Number(hours) : null,
    };
  }, [on, topKind, subCategory, investKind, propertyId, hours]);

  useEffect(() => {
    onChange?.(payload);
  }, [payload, onChange]);

  return (
    <section className="bi-surface p-4 space-y-3" data-testid="review-personal-toggle">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            on ? "bg-[#FF9500]/15 border border-[#FF9500]/40" : "bg-white/5 border border-white/10"
          }`}
        >
          <UserRound className={`w-5 h-5 ${on ? "text-[#FF9500]" : "text-white/40"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-sm">Personal expense</div>
          <div className="text-[11px] text-white/50 leading-snug">
            {on
              ? "Approving will save this straight to Personal Tax — not the business ledger."
              : "Toggle on if this is a rent statement, dividend notice, personal deduction, etc."}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          data-testid="rv-personal-switch"
          onClick={() => setOn((v) => !v)}
          className={`relative h-7 w-12 rounded-full transition ${
            on ? "bg-[#FF9500]" : "bg-white/10"
          }`}
        >
          <span
            className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition ${
              on ? "right-0.5" : "left-0.5"
            }`}
          />
        </button>
      </div>

      {on && (
        <div className="space-y-2.5 pt-1 border-t border-white/5" data-testid="rv-personal-body">
          {suggestion?.reason && (
            <div className="text-[11px] text-[#FF9500] inline-flex items-center gap-1.5" data-testid="rv-personal-hint">
              <Sparkles className="w-3.5 h-3.5" /> AI hint: {suggestion.reason}
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
              Kind
            </label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {TOP_KINDS.map((k) => {
                const Icon = k.icon;
                const active = topKind === k.key;
                return (
                  <button
                    key={k.key}
                    type="button"
                    data-testid={`rv-personal-kind-${k.key}`}
                    onClick={() => {
                      setTopKind(k.key);
                      if (k.key === "rental_income") setSubCategory("rent_gross");
                      else if (k.key === "rental_expense") setSubCategory("agent_fees");
                      else if (k.key === "deduction") setSubCategory("other");
                    }}
                    className={`h-10 rounded-lg text-[11px] font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 transition ${
                      active
                        ? "bg-[#FF9500]/20 text-[#FF9500] border border-[#FF9500]/40"
                        : "bg-white/5 border border-white/10 text-white/60 hover:text-white/90"
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${active ? "" : k.color}`} />
                    <span className="truncate">{k.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {(topKind === "rental_income" || topKind === "rental_expense") && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
                  Sub-category
                </label>
                <BiSelect
                  data-testid="rv-personal-sub"
                  className="w-full bi-input mt-1"
                  value={subCategory}
                  onChange={(e) => setSubCategory(e.target.value)}
                >
                  {(topKind === "rental_income" ? asOptions(RENTAL_INCOME_SUBS) : asOptions(RENTAL_EXPENSE_SUBS)).map((s) => (
                    <option key={s.v} value={s.v}>{s.l}</option>
                  ))}
                </BiSelect>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
                  Property
                </label>
                <BiSelect
                  data-testid="rv-personal-property"
                  className="w-full bi-input mt-1"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                >
                  {properties.length === 0 ? (
                    <option value="">No rentals — add one in Personal Tax first</option>
                  ) : (
                    properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.address}</option>
                    ))
                  )}
                </BiSelect>
              </div>
            </>
          )}

          {topKind === "investment" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
                Investment type
              </label>
              <BiSelect
                data-testid="rv-personal-invest"
                className="w-full bi-input mt-1"
                value={investKind}
                onChange={(e) => setInvestKind(e.target.value)}
              >
                {INVESTMENT_TYPES.map((k) => (
                  <option key={`${k.type}|${k.sub}`} value={`${k.type}|${k.sub}`}>{k.label}</option>
                ))}
              </BiSelect>
            </div>
          )}

          {topKind === "deduction" && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
                  Deduction type
                </label>
                <BiSelect
                  data-testid="rv-personal-sub"
                  className="w-full bi-input mt-1"
                  value={subCategory}
                  onChange={(e) => setSubCategory(e.target.value)}
                >
                  {asOptions(DEDUCTION_SUBS).map((s) => (
                    <option key={s.v} value={s.v}>{s.l}</option>
                  ))}
                </BiSelect>
              </div>
              {subCategory === "wfh_hours" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
                    Hours worked from home
                  </label>
                  <input
                    data-testid="rv-personal-hours"
                    className="w-full bi-input mt-1"
                    type="number"
                    step="0.5"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    placeholder="e.g. 150"
                  />
                  <div className="text-[11px] text-white/50 mt-1">
                    ATO fixed rate {WFH_RATE_LABEL} → <b className="text-white">${((Number(hours) || 0) * WFH_RATE_PER_HOUR).toFixed(2)}</b>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
